import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Fade,
  Grow,
  alpha,
  CircularProgress,
  Autocomplete,
  RadioGroup,
  Radio,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  Storefront as StorefrontIcon,
  AccountBalance as AccountBalanceIcon,
  BusinessCenter as BusinessCenterIcon,
} from '@mui/icons-material';
import { customerService } from '../../services/customers.service';
import { vehicleService } from '../../services/vehicles.service';
import { LeadSource, LeadStatus } from '../../types/customer';
import { activityTrackingService } from '../../services/activity-tracking.service';
import AddressAutocomplete, { ParsedAddress } from '../common/AddressAutocomplete';
import { PAYMENT_TERMS_OPTIONS } from '../../config/paymentTerms.config';
import { useXeroAccounts } from '../../hooks/useXeroAccounts';
import { OrganizationService } from '../../services/organization.service';

type ContactType = 'customer' | 'supplier' | 'finance_company' | 'dealer';

interface AddCustomerModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (customerId: string) => void;
  /**
   * Initial contact type. If provided, modal skips the type-picker step and
   * goes straight to the form. If omitted, the user picks a type first.
   */
  contactType?: ContactType;
  /** Prefill name. For business contacts → company_name. For customer → split first/last. */
  prefillName?: string;
  /** Edit mode: pass an existing customer to load fields and switch to UPDATE on save. */
  editingCustomer?: any;
}

const australianStates = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];

const CONTACT_LABELS: Record<ContactType, string> = {
  customer: 'Customer',
  supplier: 'Supplier',
  finance_company: 'Finance Company',
  dealer: 'Dealer',
};

const CONTACT_ICONS: Record<ContactType, React.ReactNode> = {
  customer: <PersonIcon fontSize="small" />,
  supplier: <StorefrontIcon fontSize="small" />,
  finance_company: <AccountBalanceIcon fontSize="small" />,
  dealer: <BusinessCenterIcon fontSize="small" />,
};

const CONTACT_TYPES: ContactType[] = ['customer', 'supplier', 'finance_company', 'dealer'];

export default function AddCustomerModal({ open, onClose, onSuccess, contactType: initialContactType, prefillName, editingCustomer }: AddCustomerModalProps) {
  const isEdit = !!editingCustomer?.id;
  const [contactType, setContactType] = useState<ContactType>(initialContactType ?? 'customer');
  const [orgIdForAccounts, setOrgIdForAccounts] = useState<string | null>(null);
  useEffect(() => {
    if (open) OrganizationService.getOrganizationId().then((id) => setOrgIdForAccounts(id || null));
  }, [open]);
  const { accounts: xeroAccounts } = useXeroAccounts(orgIdForAccounts);
  // Step 0 = type picker (only when no contactType prop). Step 1/2 = form.
  const [step, setStep] = useState<0 | 1 | 2>(initialContactType ? 1 : 0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [vehicles, setVehicles] = useState<any[]>([]);

  // Step 1 fields - Essential info
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [leadSource, setLeadSource] = useState<LeadSource>('walk_in');
  const [leadStatus, setLeadStatus] = useState<LeadStatus>('new');
  const [interestedVehicleId, setInterestedVehicleId] = useState('');

  // Business fields (for supplier/finance/dealer)
  const [companyName, setCompanyName] = useState('');
  const [abn, setAbn] = useState('');
  const [acn, setAcn] = useState('');
  const [dealerLicence, setDealerLicence] = useState('');
  const [premisesNumber, setPremisesNumber] = useState('');
  const [paymentTermsDays, setPaymentTermsDays] = useState<string>('30');
  const [defaultExpenseCategory, setDefaultExpenseCategory] = useState('');
  const [supplierAccountCode, setSupplierAccountCode] = useState('');
  const [gstRegistered, setGstRegistered] = useState(true);
  const [bankAccountName, setBankAccountName] = useState('');
  const [bsb, setBsb] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const isBusinessContact = contactType !== 'customer';

  // Step 2 fields - Additional details
  const [mobile, setMobile] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [suburb, setSuburb] = useState('');
  const [state, setState] = useState('');
  const [postcode, setPostcode] = useState('');
  const [preferredContactMethod, setPreferredContactMethod] = useState('phone');
  const [budgetMin, setBudgetMin] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [notes, setNotes] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);

  // Load vehicles on mount
  useEffect(() => {
    if (open) {
      loadVehicles();
    }
  }, [open]);

  // Reset internal type + step to prop when modal re-opens
  useEffect(() => {
    if (open) {
      // In edit mode, derive type from the existing customer's flags
      if (editingCustomer) {
        const t: ContactType = editingCustomer.is_supplier ? 'supplier'
                              : editingCustomer.is_finance_company ? 'finance_company'
                              : editingCustomer.is_dealer ? 'dealer'
                              : 'customer';
        setContactType(t);
        setStep(1); // skip type picker — type is determined
      } else {
        setContactType(initialContactType ?? 'customer');
        setStep(initialContactType ? 1 : 0);
      }
    }
  }, [open, initialContactType, editingCustomer]);

  // Edit-mode prefill — populate every field from the existing customer
  useEffect(() => {
    if (!open || !editingCustomer) return;
    setFirstName(editingCustomer.first_name || '');
    setLastName(editingCustomer.last_name === '—' ? '' : (editingCustomer.last_name || ''));
    setEmail(editingCustomer.email || '');
    setPhone(editingCustomer.phone || '');
    setMobile(editingCustomer.mobile || '');
    setDateOfBirth(editingCustomer.date_of_birth || '');
    setLicenseNumber(editingCustomer.drivers_license_number || '');
    setAddressLine1(editingCustomer.address_line1 || '');
    setAddressLine2(editingCustomer.address_line2 || '');
    setSuburb(editingCustomer.suburb || '');
    setState(editingCustomer.state || '');
    setPostcode(editingCustomer.postcode || '');
    setPreferredContactMethod(editingCustomer.preferred_contact_method || 'phone');
    setBudgetMin(editingCustomer.budget_min?.toString() || '');
    setBudgetMax(editingCustomer.budget_max?.toString() || '');
    setNotes(editingCustomer.notes || '');
    setMarketingConsent(!!editingCustomer.marketing_consent);
    setLeadSource(editingCustomer.lead_source || 'walk_in');
    setLeadStatus(editingCustomer.lead_status || 'new');
    setCompanyName(editingCustomer.company_name || '');
    setAbn(editingCustomer.abn || '');
    setAcn(editingCustomer.acn || '');
    setDealerLicence(editingCustomer.dealer_licence || '');
    setPremisesNumber(editingCustomer.premises_number || '');
    setPaymentTermsDays(editingCustomer.payment_terms_days?.toString() || '30');
    setDefaultExpenseCategory(editingCustomer.default_expense_category || '');
    setSupplierAccountCode(editingCustomer.supplier_account_code || '');
    setGstRegistered(editingCustomer.gst_registered ?? editingCustomer.is_gst_registered ?? true);
    setBankAccountName(editingCustomer.bank_account_name || '');
    setBsb(editingCustomer.bsb || '');
    setAccountNumber(editingCustomer.account_number || '');
  }, [open, editingCustomer]);

  // Apply prefillName when modal opens
  useEffect(() => {
    if (!open || !prefillName) return;
    const name = prefillName.trim();
    if (!name) return;
    if (initialContactType === 'customer') {
      const parts = name.split(' ');
      setFirstName(parts[0] || '');
      setLastName(parts.slice(1).join(' '));
    } else {
      setCompanyName(name);
      setFirstName(name);
      setLastName('—');
    }
  }, [open, prefillName, initialContactType]);

  const hasUserInput = () =>
    !!(firstName || lastName || email || phone || mobile || companyName || abn || acn ||
       addressLine1 || dealerLicence || premisesNumber || bankAccountName || bsb || accountNumber);

  const [pendingType, setPendingType] = useState<ContactType | null>(null);

  const handleTypeSwitch = (next: ContactType) => {
    if (next === contactType) return;
    if (hasUserInput()) {
      setPendingType(next);
      return;
    }
    setContactType(next);
    setStep(1);
  };

  const confirmTypeSwitch = () => {
    if (!pendingType) return;
    resetForm();
    setContactType(pendingType);
    setStep(1);
    setPendingType(null);
  };

  const loadVehicles = async () => {
    try {
      const { data } = await vehicleService.getVehicles({});
      setVehicles(data || []);
    } catch (error) {
      console.error('Error loading vehicles:', error);
    }
  };

  const handleAddressSelect = (address: ParsedAddress) => {
    setAddressLine1(address.address_line1);
    setAddressLine2(address.address_line2);
    setSuburb(address.suburb);
    setState(address.state);
    setPostcode(address.postcode);
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  const resetForm = () => {
    setStep(initialContactType ? 1 : 0);
    setError('');
    setSuccess(false);
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setLeadSource('walk_in');
    setLeadStatus('new');
    setInterestedVehicleId('');
    setMobile('');
    setDateOfBirth('');
    setLicenseNumber('');
    setAddressLine1('');
    setAddressLine2('');
    setSuburb('');
    setState('');
    setPostcode('');
    setPreferredContactMethod('phone');
    setBudgetMin('');
    setBudgetMax('');
    setNotes('');
    setMarketingConsent(false);
    setCompanyName('');
    setAbn('');
    setAcn('');
    setDealerLicence('');
    setPremisesNumber('');
    setPaymentTermsDays('30');
    setDefaultExpenseCategory('');
    setSupplierAccountCode('');
    setGstRegistered(true);
    setBankAccountName('');
    setBsb('');
    setAccountNumber('');
  };

  const validateStep1 = () => {
    if (isBusinessContact) {
      if (!companyName.trim()) return `${CONTACT_LABELS[contactType]} name is required`;
      if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return 'Please enter a valid email address';
      }
      return null;
    }
    if (!firstName.trim()) return 'First name is required';
    if (!lastName.trim()) return 'Last name is required';
    if (!email.trim() && !mobile.trim()) return 'Email or mobile is required';
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return 'Please enter a valid email address';
    }
    return null;
  };

  const handleNext = () => {
    const validationError = validateStep1();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    setStep(2);
  };

  const handleBack = () => {
    setError('');
    setStep(1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const businessFirstName = isBusinessContact && !firstName.trim() ? companyName.trim() : firstName.trim();
      const businessLastName = isBusinessContact && !lastName.trim() ? '—' : lastName.trim();

      const customerData: any = {
        first_name: businessFirstName,
        last_name: businessLastName,
        lead_source: isBusinessContact ? 'other' : leadSource,
        lead_status: isBusinessContact ? 'won' : leadStatus,
        lead_temperature: isBusinessContact ? undefined : 'warm',
        marketing_consent: marketingConsent,
        is_active: true,
        type: contactType === 'supplier' ? 'supplier' : 'customer',
        is_supplier: contactType === 'supplier',
        is_finance_company: contactType === 'finance_company',
        is_dealer: contactType === 'dealer',
      };

      if (companyName.trim()) customerData.company_name = companyName.trim();
      if (abn.trim()) customerData.abn = abn.trim();
      if (acn.trim()) customerData.acn = acn.trim();
      if (dealerLicence.trim()) customerData.dealer_licence = dealerLicence.trim();
      if (premisesNumber.trim()) customerData.premises_number = premisesNumber.trim();

      // Supplier-specific
      if (contactType === 'supplier') {
        const days = parseInt(paymentTermsDays, 10);
        if (!Number.isNaN(days)) customerData.payment_terms_days = days;
        if (defaultExpenseCategory.trim()) customerData.default_expense_category = defaultExpenseCategory.trim();
        if (supplierAccountCode.trim()) customerData.supplier_account_code = supplierAccountCode.trim();
        customerData.gst_registered = gstRegistered;
        customerData.is_gst_registered = gstRegistered;
      }

      // Bank fields (supplier/finance/dealer)
      if (isBusinessContact) {
        if (bankAccountName.trim()) customerData.bank_account_name = bankAccountName.trim();
        if (bsb.trim()) customerData.bsb = bsb.trim();
        if (accountNumber.trim()) customerData.account_number = accountNumber.trim();
      }

      // Add optional fields if provided
      if (email.trim()) customerData.email = email.trim();
      if (phone.trim()) customerData.phone = phone.trim();
      if (mobile.trim()) customerData.mobile = mobile.trim();
      if (dateOfBirth) customerData.date_of_birth = dateOfBirth;
      if (licenseNumber.trim()) customerData.drivers_license_number = licenseNumber.trim();
      if (addressLine1.trim()) customerData.address_line1 = addressLine1.trim();
      if (addressLine2.trim()) customerData.address_line2 = addressLine2.trim();
      if (suburb.trim()) customerData.suburb = suburb.trim();
      if (state) customerData.state = state;
      if (postcode.trim()) customerData.postcode = postcode.trim();
      if (preferredContactMethod) customerData.preferred_contact_method = preferredContactMethod;
      if (budgetMin) customerData.budget_min = parseFloat(budgetMin);
      if (budgetMax) customerData.budget_max = parseFloat(budgetMax);
      if (notes.trim()) customerData.notes = notes.trim();

      const result = isEdit
        ? await customerService.updateCustomer(editingCustomer.id, customerData)
        : await customerService.createCustomer(customerData);

      if (result.error) {
        throw new Error(result.error.message || (isEdit ? 'Failed to update contact' : 'Failed to create customer'));
      }

      const newCustomer = result.data || (isEdit ? { ...editingCustomer, ...customerData } : null);

      if (newCustomer) {
        // Track customer creation activity
        await activityTrackingService.logActivity('customers', 'create', {
          resourceType: 'customer',
          resourceId: newCustomer.id,
          resourceName: `${newCustomer.first_name} ${newCustomer.last_name}`,
          metadata: {
            leadSource: leadSource,
            leadStatus: leadStatus,
            hasEmail: !!email,
            hasPhone: !!phone,
          }
        });

        // Add vehicle interest if selected
        if (interestedVehicleId) {
          try {
            await customerService.addVehicleInterest({
              customer_id: newCustomer.id,
              vehicle_id: interestedVehicleId,
              priority: 2,
              notes: ''
            });
          } catch (vehicleError) {
            console.warn('Failed to add vehicle interest:', vehicleError);
          }
        }

        setSuccess(true);

        // Show success for 1.5 seconds then close
        setTimeout(() => {
          onSuccess(newCustomer.id);
          handleClose();
        }, 1500);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create customer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        }
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={handleClose}
        disabled={loading}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          zIndex: 1,
          color: 'text.secondary',
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: 0 }}>
        {success ? (
          // Success state
          <Fade in={success}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 8,
                px: 4,
              }}
            >
              <CheckCircleIcon
                sx={{
                  fontSize: 80,
                  color: 'success.main',
                  mb: 2,
                }}
              />
              <Typography variant="h5" fontWeight={600} gutterBottom>
                {isEdit ? `${CONTACT_LABELS[contactType]} Updated` : `${CONTACT_LABELS[contactType]} Added`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isEdit ? 'Changes saved successfully' : `Successfully created ${CONTACT_LABELS[contactType].toLowerCase()} profile`}
              </Typography>
            </Box>
          </Fade>
        ) : (
          <Box sx={{ p: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={600} gutterBottom>
                {isEdit ? `Edit ${CONTACT_LABELS[contactType]}` : (step === 0 ? 'Create New Contact' : step === 1 ? `New ${CONTACT_LABELS[contactType]}` : 'Additional Details')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {step === 0
                  ? 'What kind of contact are you adding?'
                  : step === 1
                  ? 'Enter the essentials'
                  : `Complete the ${CONTACT_LABELS[contactType].toLowerCase()} profile (optional)`}
              </Typography>
            </Box>

            {/* Type chip strip — only on form steps (1, 2). Switches type, with confirm if data entered. */}
            {step !== 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <ToggleButtonGroup
                  value={contactType}
                  exclusive
                  onChange={(_, val) => val && handleTypeSwitch(val as ContactType)}
                  size="small"
                  sx={{
                    flexWrap: 'wrap',
                    gap: 0.5,
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      borderRadius: '999px !important',
                      border: '1px solid',
                      borderColor: 'divider',
                      px: 2,
                      py: 0.5,
                      gap: 0.75,
                      fontSize: '0.8rem',
                      fontWeight: 500,
                    },
                    '& .Mui-selected': {
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                      borderColor: 'primary.main',
                      color: 'primary.main',
                    },
                  }}
                >
                  {CONTACT_TYPES.map((t) => (
                    <ToggleButton key={t} value={t}>
                      {CONTACT_ICONS[t]}
                      {CONTACT_LABELS[t]}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            )}

            {/* Step indicator (hidden on type-picker step) */}
            {step !== 0 && (
            <Box sx={{ display: 'flex', gap: 1, mb: 4, justifyContent: 'center' }}>
              <Box
                sx={{
                  width: 32,
                  height: 4,
                  borderRadius: 2,
                  bgcolor: step === 1 ? 'primary.main' : alpha('#000', 0.1),
                  transition: 'all 0.3s ease',
                }}
              />
              <Box
                sx={{
                  width: 32,
                  height: 4,
                  borderRadius: 2,
                  bgcolor: step === 2 ? 'primary.main' : alpha('#000', 0.1),
                  transition: 'all 0.3s ease',
                }}
              />
            </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {/* Step 0: Type picker — big cards, click to advance */}
            {step === 0 && (
              <Grow in={step === 0}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  {CONTACT_TYPES.map((t) => (
                    <Button
                      key={t}
                      variant="outlined"
                      onClick={() => {
                        setContactType(t);
                        setStep(1);
                      }}
                      sx={{
                        py: 3,
                        px: 2,
                        borderRadius: 3,
                        borderWidth: 2,
                        textTransform: 'none',
                        flexDirection: 'column',
                        gap: 1,
                        color: 'text.primary',
                        borderColor: 'divider',
                        '&:hover': {
                          borderWidth: 2,
                          borderColor: 'primary.main',
                          bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                        },
                      }}
                    >
                      <Box sx={{ fontSize: 32, color: 'primary.main', display: 'flex' }}>
                        {React.cloneElement(CONTACT_ICONS[t] as React.ReactElement, { fontSize: 'large' })}
                      </Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {CONTACT_LABELS[t]}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                        {t === 'customer' && 'Lead, buyer, or service customer'}
                        {t === 'supplier' && 'Parts, freight, services, government'}
                        {t === 'finance_company' && 'Lender, floorplan, payout'}
                        {t === 'dealer' && 'Wholesale dealer or trade-in source'}
                      </Typography>
                    </Button>
                  ))}
                </Box>
              </Grow>
            )}

            {/* Step 1: Essential Info */}
            {step === 1 && (
              <Grow in={step === 1}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {/* Company fields for business contacts */}
                  {isBusinessContact && (
                    <>
                      <TextField
                        fullWidth
                        label={contactType === 'supplier' ? 'Supplier Name' : contactType === 'finance_company' ? 'Finance Company Name' : 'Dealership Name'}
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        required
                        autoFocus
                        variant="outlined"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                      <TextField
                        fullWidth
                        label="ABN"
                        value={abn}
                        onChange={(e) => setAbn(e.target.value)}
                        variant="outlined"
                        placeholder="11 digit ABN"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />

                      {/* Dealer & finance: ACN */}
                      {(contactType === 'dealer' || contactType === 'finance_company') && (
                        <TextField
                          fullWidth
                          label="ACN"
                          value={acn}
                          onChange={(e) => setAcn(e.target.value)}
                          variant="outlined"
                          placeholder="9 digit ACN"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                      )}

                      {contactType === 'dealer' && (
                        <>
                          <TextField
                            fullWidth
                            label="Dealer Licence"
                            value={dealerLicence}
                            onChange={(e) => setDealerLicence(e.target.value)}
                            variant="outlined"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                          <TextField
                            fullWidth
                            label="Premises Number"
                            value={premisesNumber}
                            onChange={(e) => setPremisesNumber(e.target.value)}
                            variant="outlined"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                        </>
                      )}

                      {/* Supplier: payment terms inline */}
                      {contactType === 'supplier' && (
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <FormControl fullWidth>
                            <InputLabel>Payment Terms</InputLabel>
                            <Select
                              value={paymentTermsDays}
                              label="Payment Terms"
                              onChange={(e) => setPaymentTermsDays(String(e.target.value))}
                              sx={{ borderRadius: 2 }}
                            >
                              {PAYMENT_TERMS_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={String(opt.value)}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={gstRegistered}
                                onChange={(e) => setGstRegistered(e.target.checked)}
                              />
                            }
                            label="GST Registered"
                            sx={{ ml: 0, mr: 0, whiteSpace: 'nowrap' }}
                          />
                        </Box>
                      )}
                    </>
                  )}

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      fullWidth
                      label={isBusinessContact ? 'Contact First Name (optional)' : 'First Name'}
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required={!isBusinessContact}
                      autoFocus={!isBusinessContact}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                    <TextField
                      fullWidth
                      label={isBusinessContact ? 'Contact Last Name (optional)' : 'Last Name'}
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required={!isBusinessContact}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Box>

                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Mobile"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />

                  {/* Customer-only fields — hide for business contacts */}
                  {!isBusinessContact && (
                    <>
                      <FormControl fullWidth>
                        <InputLabel>Lead Source</InputLabel>
                        <Select
                          value={leadSource}
                          label="Lead Source"
                          onChange={(e) => setLeadSource(e.target.value as LeadSource)}
                          sx={{ borderRadius: 2 }}
                        >
                          <MenuItem value="walk_in">Walk In</MenuItem>
                          <MenuItem value="phone">Phone</MenuItem>
                          <MenuItem value="website">Website</MenuItem>
                          <MenuItem value="email">Email</MenuItem>
                          <MenuItem value="referral">Referral</MenuItem>
                          <MenuItem value="carsales">Carsales</MenuItem>
                          <MenuItem value="facebook">Facebook</MenuItem>
                          <MenuItem value="instagram">Instagram</MenuItem>
                          <MenuItem value="google">Google</MenuItem>
                          <MenuItem value="other">Other</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl fullWidth>
                        <InputLabel>Lead Status</InputLabel>
                        <Select
                          value={leadStatus}
                          label="Lead Status"
                          onChange={(e) => setLeadStatus(e.target.value as LeadStatus)}
                          sx={{ borderRadius: 2 }}
                        >
                          <MenuItem value="new">New</MenuItem>
                          <MenuItem value="contacted">Contacted</MenuItem>
                          <MenuItem value="qualified">Qualified</MenuItem>
                          <MenuItem value="test_drive">Test Drive</MenuItem>
                          <MenuItem value="negotiation">Negotiation</MenuItem>
                        </Select>
                      </FormControl>
                    </>
                  )}

                  {!isBusinessContact && <Autocomplete
                    fullWidth
                    options={vehicles}
                    getOptionLabel={(option) =>
                      `${option.year} ${option.make} ${option.model} - $${(option.sale_price || option.list_price)?.toLocaleString() || 'POA'}`
                    }
                    value={vehicles.find(v => v.id === interestedVehicleId) || null}
                    onChange={(_, newValue) => setInterestedVehicleId(newValue?.id || '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Interested Vehicle (Optional)"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                      />
                    )}
                  />}

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleNext}
                    disabled={loading}
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      mt: 1,
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                    }}
                  >
                    Continue
                  </Button>

                  <Button
                    fullWidth
                    variant="text"
                    size="large"
                    onClick={handleSubmit}
                    disabled={
                      loading ||
                      (isBusinessContact
                        ? !companyName.trim()
                        : !firstName.trim() || !lastName.trim() || (!email.trim() && !mobile.trim()))
                    }
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      color: 'text.secondary',
                    }}
                  >
                    {isEdit ? 'Save Changes' : `Skip & Add ${CONTACT_LABELS[contactType]}`}
                  </Button>
                </Box>
              </Grow>
            )}

            {/* Step 2: Additional Details */}
            {step === 2 && (
              <Grow in={step === 2}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    variant="outlined"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />

                  {/* Customer-only: DOB + License */}
                  {!isBusinessContact && (
                    <>
                      <TextField
                        fullWidth
                        label="Date of Birth"
                        type="date"
                        value={dateOfBirth}
                        onChange={(e) => setDateOfBirth(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                      <TextField
                        fullWidth
                        label="License Number"
                        value={licenseNumber}
                        onChange={(e) => setLicenseNumber(e.target.value)}
                        variant="outlined"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </>
                  )}

                  {/* Address — shared */}
                  <AddressAutocomplete
                    fullWidth
                    label="Address"
                    value={addressLine1}
                    onAddressSelect={handleAddressSelect}
                    onChange={(value) => setAddressLine1(value)}
                    placeholder="Start typing address..."
                    variant="outlined"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />

                  <TextField
                    fullWidth
                    label="Address Line 2"
                    value={addressLine2}
                    onChange={(e) => setAddressLine2(e.target.value)}
                    variant="outlined"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Suburb"
                      value={suburb}
                      onChange={(e) => setSuburb(e.target.value)}
                      variant="outlined"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <FormControl fullWidth>
                      <InputLabel>State</InputLabel>
                      <Select
                        value={state}
                        label="State"
                        onChange={(e) => setState(e.target.value)}
                        sx={{ borderRadius: 2 }}
                      >
                        {australianStates.map((s) => (
                          <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Postcode"
                      value={postcode}
                      onChange={(e) => setPostcode(e.target.value)}
                      variant="outlined"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, width: 120 }}
                    />
                  </Box>

                  {/* Business contacts: bank + (supplier) Xero/category */}
                  {isBusinessContact && (
                    <>
                      {contactType === 'supplier' && (
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <TextField
                            fullWidth
                            label="Default Expense Category"
                            value={defaultExpenseCategory}
                            onChange={(e) => setDefaultExpenseCategory(e.target.value)}
                            placeholder="e.g. Parts, Freight, Detailing"
                            variant="outlined"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                          <Autocomplete
                            fullWidth
                            options={xeroAccounts}
                            value={xeroAccounts.find((a) => a.code === supplierAccountCode) || null}
                            onChange={(_, v) => setSupplierAccountCode(v?.code || '')}
                            groupBy={(option) => option.category}
                            getOptionLabel={(option) => `${option.code} — ${option.name}`}
                            isOptionEqualToValue={(o, v) => o.code === v.code}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Default Xero Account"
                                placeholder="Select GL account"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                              />
                            )}
                          />
                        </Box>
                      )}

                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                        Bank Details (optional)
                      </Typography>
                      <TextField
                        fullWidth
                        label="Account Name"
                        value={bankAccountName}
                        onChange={(e) => setBankAccountName(e.target.value)}
                        variant="outlined"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                          label="BSB"
                          value={bsb}
                          onChange={(e) => setBsb(e.target.value)}
                          placeholder="000-000"
                          variant="outlined"
                          sx={{ width: 160, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                        <TextField
                          fullWidth
                          label="Account Number"
                          value={accountNumber}
                          onChange={(e) => setAccountNumber(e.target.value)}
                          variant="outlined"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                      </Box>
                    </>
                  )}

                  {/* Customer-only: Contact preference + Budget */}
                  {!isBusinessContact && (
                    <>
                      <FormControl fullWidth>
                        <InputLabel>Preferred Contact Method</InputLabel>
                        <Select
                          value={preferredContactMethod}
                          label="Preferred Contact Method"
                          onChange={(e) => setPreferredContactMethod(e.target.value)}
                          sx={{ borderRadius: 2 }}
                        >
                          <MenuItem value="phone">Phone</MenuItem>
                          <MenuItem value="email">Email</MenuItem>
                          <MenuItem value="sms">SMS</MenuItem>
                          <MenuItem value="any">Any</MenuItem>
                        </Select>
                      </FormControl>

                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                          fullWidth
                          label="Budget Min"
                          type="number"
                          value={budgetMin}
                          onChange={(e) => setBudgetMin(e.target.value)}
                          variant="outlined"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                        <TextField
                          fullWidth
                          label="Budget Max"
                          type="number"
                          value={budgetMax}
                          onChange={(e) => setBudgetMax(e.target.value)}
                          variant="outlined"
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                      </Box>
                    </>
                  )}

                  {/* Notes — shared */}
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    variant="outlined"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />

                  {/* Customer-only: Marketing consent */}
                  {!isBusinessContact && (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={marketingConsent}
                          onChange={(e) => setMarketingConsent(e.target.checked)}
                        />
                      }
                      label="Marketing consent"
                    />
                  )}

                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={handleBack}
                      disabled={loading}
                      startIcon={<ArrowBackIcon />}
                      sx={{
                        borderRadius: 2,
                        py: 1.5,
                        textTransform: 'none',
                        fontSize: '1rem',
                        fontWeight: 600,
                        flex: 1,
                      }}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleSubmit}
                      disabled={loading}
                      sx={{
                        borderRadius: 2,
                        py: 1.5,
                        textTransform: 'none',
                        fontSize: '1rem',
                        fontWeight: 600,
                        flex: 2,
                      }}
                    >
                      {loading ? <CircularProgress size={24} color="inherit" /> : (isEdit ? 'Save Changes' : `Add ${CONTACT_LABELS[contactType]}`)}
                    </Button>
                  </Box>
                </Box>
              </Grow>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Type-switch confirmation */}
      <Dialog
        open={!!pendingType}
        onClose={() => setPendingType(null)}
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
          Switch contact type?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Switching to <strong>{pendingType ? CONTACT_LABELS[pendingType] : ''}</strong> will clear the current form. This can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPendingType(null)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={confirmTypeSwitch}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Switch & clear
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
